import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import type { ServiceCategoriesQuery, ServicesQuery } from "@/gql/graphql";
import { SERVICE_CATEGORIES_QUERY, SERVICES_QUERY } from "@/graphql/services";
import { useSalonStore } from "@/lib/store/salon";

type ServiceItem = NonNullable<ServicesQuery["services"]>[number];
type CategoryItem = NonNullable<ServiceCategoriesQuery["serviceCategories"]>[number];

interface UseDiscountServicesReturn {
  services: ServiceItem[];
  categories: CategoryItem[];
  loading: boolean;
  error: Error | null;
  searchServices: (query: string) => ServiceItem[];
  searchCategories: (query: string) => CategoryItem[];
}

/**
 * Hook for fetching and searching services and categories for discounts
 */
export function useDiscountServices(): UseDiscountServicesReturn {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id || "";

  const {
    data: servicesData,
    loading: servicesLoading,
    error: servicesError,
  } = useQuery(SERVICES_QUERY, {
    variables: { salonId },
    skip: !salonId,
  });

  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useQuery(SERVICE_CATEGORIES_QUERY, {
    variables: { salonId },
    skip: !salonId,
  });

  const services = useMemo(() => {
    return (servicesData?.services || []).filter((s) => s.isActive);
  }, [servicesData?.services]);

  const categories = useMemo(() => {
    return (categoriesData?.serviceCategories || []).filter((c) => !c.isArchived);
  }, [categoriesData?.serviceCategories]);

  const searchServices = useCallback(
    (query: string): ServiceItem[] => {
      if (!query.trim()) return services;
      const lowerQuery = query.toLowerCase();
      return services.filter(
        (service) =>
          service.name.toLowerCase().includes(lowerQuery) ||
          service.serviceCode?.toLowerCase().includes(lowerQuery)
      );
    },
    [services]
  );

  const searchCategories = useCallback(
    (query: string): CategoryItem[] => {
      if (!query.trim()) return categories;
      const lowerQuery = query.toLowerCase();
      return categories.filter((category) => category.name.toLowerCase().includes(lowerQuery));
    },
    [categories]
  );

  return {
    services,
    categories,
    loading: servicesLoading || categoriesLoading,
    error: servicesError || categoriesError || null,
    searchServices,
    searchCategories,
  };
}
